// features/projects/templates/continuum/simulate.js
// ═══════════════════════════════════════════════════════════
// CONTINUUM SIMULATE TAB — Lab Phase 5.5 surface
//
// Surfaces the Lab simulation pipeline alongside the existing
// Cascade tab. Reads from data/lab/findings-published.js (the
// committed source-of-truth for project status + publishable
// findings).
//
// Sections:
//   1. Phase 1 build sequence (7 sessions)
//   2. Project status — population-decline first
//   3. Per-card pipeline panels (5 cards)
//   4. Published findings (initially empty)
//   5. How-to-run reference (CLI commands)
//
// Read-only v1. Tim runs the CLIs locally; findings get
// promoted to publishable status manually after calibration
// passes.
//
// References:
//   docs/LAB_METHODOLOGY_V2.md
//   docs/LAB_PROJECT_POPULATION_DECLINE.md
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';
import { C, FONT as F } from './tokens';
import {
  PUBLISHED_FINDINGS,
  PROJECTS_STATUS,
  PHASE_1_BUILD_STATUS,
} from '../../../../data/lab/findings-published';

export default function ContinuumSimulate({ workspaceId, userId }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'auto',
        background: C.bg,
        color: C.text,
        fontFamily: F.sans,
      }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 40px 80px' }}>
        <Header />
        <BuildSequenceStrip />
        <ProjectsStatus />
        <PublishedFindings />
        <HowToRun />
      </div>
    </div>
  );
}

// ─── Header ─────────────────────────────────────────────
function Header() {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ fontSize: 10, color: C.static, fontFamily: F.mono, letterSpacing: '0.1em', marginBottom: 8 }}>
        LAB · PHASE 5.5 · SIMULATION-BACKED PHASE A
      </div>
      <div style={{ fontFamily: F.serif, fontSize: 36, fontWeight: 300, lineHeight: 1.1, marginBottom: 12 }}>
        Simulate
      </div>
      <div style={{ fontSize: 14, color: C.mid, lineHeight: 1.6, maxWidth: 800 }}>
        Multi-agent simulation as a parallel research move alongside Phase A red-team.
        Stimulus-card-gated per <code style={{ color: C.gold, fontFamily: F.mono, fontSize: 12 }}>docs/LAB_METHODOLOGY_V2.md</code>.
        First project: population-decline. First finding requires calibration vs Korea TFR 2024 ± 0.10.
      </div>
    </div>
  );
}

// ─── Phase 1 build sequence strip ────────────────────────
function BuildSequenceStrip() {
  return (
    <section style={{ marginBottom: 48 }}>
      <SectionHeading number="01" label="Phase 1 Build Sequence" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {PHASE_1_BUILD_STATUS.map((step) => (
          <BuildStepRow key={step.session} step={step} />
        ))}
      </div>
    </section>
  );
}

function BuildStepRow({ step }) {
  const isShipped = step.status === 'shipped';
  const color = isShipped ? C.sage : C.dim;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '90px 1fr 110px',
        alignItems: 'center',
        gap: 16,
        padding: '10px 14px',
        background: C.s2,
        border: `1px solid ${C.b1}`,
        borderRadius: 4,
      }}
    >
      <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em' }}>
        Session {step.session}
      </div>
      <div style={{ fontSize: 12, color: C.text }}>{step.label}</div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <StatusBadge color={color} label={isShipped ? '✓ SHIPPED' : 'PENDING'} filled={isShipped} />
      </div>
    </div>
  );
}

// ─── Projects Status ─────────────────────────────────────
function ProjectsStatus() {
  return (
    <section style={{ marginBottom: 48 }}>
      <SectionHeading number="02" label="Projects" sublabel={`${PROJECTS_STATUS.length} project · stimulus-card-gated`} />
      {PROJECTS_STATUS.map((project) => (
        <ProjectPanel key={project.project_id} project={project} />
      ))}
    </section>
  );
}

function ProjectPanel({ project }) {
  return (
    <div style={{ background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 4, padding: '20px 24px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: F.serif, fontSize: 22, fontWeight: 400 }}>
          {project.project_label}
        </div>
        <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em' }}>
          Methodology {project.methodology_version} · {project.cards.length} cards · ~${project.total_estimated_cost_usd.toFixed(0)} full-project cost
        </div>
      </div>

      <div style={{ fontSize: 11, color: C.mid, marginBottom: 16, fontStyle: 'italic' }}>
        Calibration target: {project.calibration_target.primary}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {project.cards.map((card) => (
          <CardRow key={card.card_id} card={card} />
        ))}
      </div>
    </div>
  );
}

function CardRow({ card }) {
  const [expanded, setExpanded] = useState(false);
  const stages = [
    { key: 'card_locked', label: 'Card locked' },
    { key: 'agents_generated', label: 'Agents generated' },
    { key: 'runs_completed', label: 'Runs completed' },
    { key: 'aggregated', label: 'Aggregated' },
    { key: 'calibrated', label: 'Calibrated' },
    { key: 'published', label: 'Published' },
  ];

  const completedStages = stages.filter((s) => card.pipeline[s.key]).length;
  const isKeystone = card.keystone;

  return (
    <div
      style={{
        background: C.s1,
        border: `1px solid ${isKeystone ? C.gold : C.b2}`,
        borderRadius: 4,
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'grid',
          gridTemplateColumns: '160px 1fr 200px 100px',
          alignItems: 'center',
          gap: 14,
          width: '100%',
          padding: '12px 16px',
          background: 'transparent',
          border: 'none',
          color: C.text,
          textAlign: 'left',
          cursor: 'pointer',
          fontFamily: F.sans,
        }}
      >
        <div>
          <div style={{ fontSize: 11, color: isKeystone ? C.gold : C.mid, fontFamily: F.mono, letterSpacing: '0.04em' }}>
            {card.card_id}{isKeystone && <span style={{ color: C.gold, marginLeft: 6 }}>★ KEYSTONE</span>}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 13, color: C.text, marginBottom: 2 }}>{card.title}</div>
          <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono }}>
            {card.linked_hypothesis} · {card.arms} arms × {card.agents_per_arm} = {card.total_calls.toLocaleString()} calls
          </div>
        </div>
        <PipelineProgress stages={stages} card={card} />
        <div style={{ fontSize: 10, color: C.static, fontFamily: F.mono, textAlign: 'right' }}>
          ~${card.estimated_cost_usd.toFixed(2)}
        </div>
      </button>
      {expanded && <CardDetail card={card} />}
    </div>
  );
}

function PipelineProgress({ stages, card }) {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      {stages.map((s) => {
        const done = card.pipeline[s.key];
        return (
          <span
            key={s.key}
            title={s.label}
            style={{
              width: 12,
              height: 12,
              borderRadius: 2,
              background: done ? C.sage : 'transparent',
              border: `1px solid ${done ? C.sage : C.b3}`,
              display: 'inline-block',
            }}
          />
        );
      })}
      <span style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, marginLeft: 6 }}>
        {stages.filter((s) => card.pipeline[s.key]).length}/{stages.length}
      </span>
    </div>
  );
}

function CardDetail({ card }) {
  return (
    <div style={{ borderTop: `1px solid ${C.b1}`, padding: '14px 18px', background: C.s2 }}>
      {card.latest_verdict && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em', marginBottom: 4 }}>
            LATEST VERDICT
          </div>
          <div style={{ fontSize: 12, color: C.text }}>{card.latest_verdict}</div>
        </div>
      )}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em', marginBottom: 4 }}>
          RUN COMMAND
        </div>
        <code style={{ display: 'block', fontSize: 11, color: C.gold, fontFamily: F.mono, background: C.bg, padding: '8px 10px', borderRadius: 3, border: `1px solid ${C.b3}` }}>
          {card.run_command}
        </code>
      </div>
    </div>
  );
}

// ─── Published Findings ──────────────────────────────────
function PublishedFindings() {
  return (
    <section style={{ marginBottom: 48 }}>
      <SectionHeading number="03" label="Published findings" sublabel={`${PUBLISHED_FINDINGS.length} finding${PUBLISHED_FINDINGS.length === 1 ? '' : 's'} — calibration-passed`} />
      {PUBLISHED_FINDINGS.length === 0 ? (
        <div
          style={{
            padding: '20px 24px',
            background: C.s1,
            border: `1px dashed ${C.b3}`,
            borderRadius: 4,
            fontSize: 12,
            color: C.mid,
            lineHeight: 1.6,
          }}
        >
          <strong style={{ color: C.gold }}>No findings published yet.</strong>
          <br /><br />
          The first publishable finding requires:
          <ol style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
            <li>Real Claude calls run (not <code style={{ fontFamily: F.mono, color: C.amber }}>--dry-run</code>)</li>
            <li>Calibration vs Korea TFR 2024 ± 0.10 PASSED</li>
            <li>Phase 1 / Path A acquisition explicitly disclosed</li>
            <li>Manual publish step: commit a finding entry to <code style={{ fontFamily: F.mono, color: C.gold }}>data/lab/findings-published.js</code></li>
          </ol>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {PUBLISHED_FINDINGS.map((f) => (
            <FindingRow key={f.finding_id} finding={f} />
          ))}
        </div>
      )}
    </section>
  );
}

function FindingRow({ finding }) {
  const verdictColor = finding.verdict === 'PREDICTION_MATCHED' ? C.sage
    : finding.verdict === 'FALSIFIER_TRIGGERED' ? C.terra
    : C.amber;
  const calStatusLabel = (finding.calibration_status || 'calibrated').toUpperCase();
  return (
    <div style={{ background: C.s2, border: `1px solid ${verdictColor}`, borderLeft: `3px solid ${verdictColor}`, borderRadius: 4, padding: '16px 20px' }}>
      {/* Header: ID + card + status pills */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: C.gold, fontFamily: F.mono }}>{finding.finding_id}</span>
        <span style={{ fontSize: 13, color: C.text }}>{finding.card_id} · {finding.linked_hypothesis_id}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {finding.epistemic_status && (
            <span style={{ fontSize: 10, color: C.gold, fontFamily: F.mono, padding: '2px 7px', border: `1px solid ${C.gold}`, borderRadius: 3 }}>
              {finding.epistemic_status.replace(/_/g, ' ').toUpperCase()}
            </span>
          )}
          <span style={{ fontSize: 10, color: verdictColor, fontFamily: F.mono, padding: '2px 7px', border: `1px solid ${verdictColor}`, borderRadius: 3 }}>
            {finding.verdict || calStatusLabel}
          </span>
        </div>
      </div>

      {/* Headline */}
      {finding.headline && (
        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.55, marginBottom: 12, fontFamily: F.serif, fontStyle: 'italic' }}>
          {finding.headline}
        </div>
      )}

      {/* Arm comparison strip */}
      {finding.arm_A && finding.arm_B && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12, padding: '10px 12px', background: C.s1, borderRadius: 3, border: `1px solid ${C.b3}` }}>
          <ArmStat label={finding.arm_A.arm_label} value={finding.arm_A.tfr_equivalent_intent} n={finding.arm_A.n} color={C.terra} />
          <ArmStat label={finding.arm_B.arm_label} value={finding.arm_B.tfr_equivalent_intent} n={finding.arm_B.n} color={C.sage} />
          <DiffStat
            differential={finding.differential_B_minus_A}
            range={finding.pre_registered_range}
            triggered={finding.falsifier_triggered}
          />
        </div>
      )}

      {/* Caveats — first-class content (two-lens honesty) */}
      {finding.publish_caveats && finding.publish_caveats.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ fontSize: 10, fontFamily: F.mono, color: C.amber, cursor: 'pointer', letterSpacing: '0.04em' }}>
            ▸ {finding.publish_caveats.length} CAVEAT{finding.publish_caveats.length === 1 ? '' : 'S'} (read before citing)
          </summary>
          <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 18, fontSize: 11, color: C.mid, lineHeight: 1.55 }}>
            {finding.publish_caveats.map((c, i) => (
              <li key={i} style={{ marginBottom: 6 }}>{c}</li>
            ))}
          </ul>
        </details>
      )}

      {/* Footer meta */}
      {(finding.cost_usd != null || finding.timestamp_run) && (
        <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 10, color: C.static, fontFamily: F.mono }}>
          {finding.cost_usd != null && <span>cost ${finding.cost_usd.toFixed(2)}</span>}
          {finding.runtime_seconds != null && <span>runtime {finding.runtime_seconds}s</span>}
          {finding.timestamp_run && <span>run {finding.timestamp_run}</span>}
          {finding.acquisition_path && <span>path {finding.acquisition_path}</span>}
        </div>
      )}
    </div>
  );
}

function ArmStat({ label, value, n, color }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: C.static, fontFamily: F.mono, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 18, color, fontFamily: F.serif }}>{value?.toFixed?.(2) ?? value}</div>
      <div style={{ fontSize: 9, color: C.static, fontFamily: F.mono }}>n={n}</div>
    </div>
  );
}

function DiffStat({ differential, range, triggered }) {
  const inRange = range && differential >= range[0] && differential <= range[1];
  const color = triggered ? C.terra : inRange ? C.sage : C.amber;
  return (
    <div>
      <div style={{ fontSize: 9, color: C.static, fontFamily: F.mono, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 3 }}>Δ (B − A) · pre-registered</div>
      <div style={{ fontSize: 18, color, fontFamily: F.serif }}>{differential >= 0 ? '+' : ''}{differential?.toFixed?.(2) ?? differential}</div>
      <div style={{ fontSize: 9, color: C.static, fontFamily: F.mono }}>
        {range ? `in [${range[0]}, ${range[1]}]` : 'no range'}
      </div>
    </div>
  );
}

// ─── How To Run ─────────────────────────────────────────
function HowToRun() {
  const steps = [
    { label: 'Validate stimulus cards (engine-gate)', cmd: 'node scripts/lab/validate-cards.js' },
    { label: 'Generate agent population (no API)', cmd: 'node scripts/lab/generate-agents.js SC-PD-001' },
    { label: 'Inspect a sample prompt (no API)', cmd: 'node scripts/lab/render-prompt.js SC-PD-001 arm_A 0' },
    { label: 'Smoke test (real Claude calls, ~$0.20)', cmd: 'node scripts/lab/run-stimulus.js SC-PD-001 --limit 50 --yes' },
    { label: 'Aggregate + verdict', cmd: 'node scripts/lab/aggregate-runs.js SC-PD-001' },
    { label: 'Calibrate against Korea TFR 2024', cmd: 'node scripts/lab/calibrate.js SC-PD-001' },
    { label: 'Full run (after smoke validates)', cmd: 'node scripts/lab/run-stimulus.js SC-PD-001 --yes' },
  ];
  return (
    <section style={{ marginBottom: 48 }}>
      <SectionHeading number="04" label="How to run" sublabel="local CLI · ANTHROPIC_API_KEY in .env.local" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {steps.map((step, idx) => (
          <div
            key={idx}
            style={{
              display: 'grid',
              gridTemplateColumns: '36px 1fr',
              gap: 14,
              padding: '10px 14px',
              background: C.s1,
              border: `1px solid ${C.b2}`,
              borderRadius: 3,
            }}
          >
            <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em' }}>
              {String(idx + 1).padStart(2, '0')}
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.mid, marginBottom: 4 }}>{step.label}</div>
              <code style={{ fontSize: 11, color: C.gold, fontFamily: F.mono }}>{step.cmd}</code>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Shared atoms ────────────────────────────────────────
function SectionHeading({ number, label, sublabel }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16 }}>
      <span style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em' }}>{number}</span>
      <span style={{ fontFamily: F.serif, fontSize: 22, fontWeight: 400, color: C.text }}>{label}</span>
      {sublabel && (
        <span style={{ fontSize: 10, color: C.static, fontFamily: F.mono, letterSpacing: '0.04em', marginLeft: 6 }}>
          {sublabel}
        </span>
      )}
    </div>
  );
}

function StatusBadge({ color, label, filled }) {
  return (
    <span
      style={{
        fontSize: 9,
        fontFamily: F.mono,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        padding: '3px 8px',
        borderRadius: 3,
        border: `1px solid ${color}`,
        background: filled ? hexAlpha(color, 0.1) : 'transparent',
        color: color,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

function hexAlpha(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
